import { Trans } from "react-i18next";import { useRef, useEffect } from "react";
import { ChevronDownIcon } from "./Icons";

function getSubModuleState(sm, selected) {
  const ids = sm.permissions.map((p) => p.id);
  const count = ids.filter((id) => selected.has(id)).length;
  if (count === 0) return "unchecked";
  if (count === ids.length) return "checked";
  return "indeterminate";
}

function getModuleState(module, selected) {
  const ids = module.sub_modules.flatMap((sm) => sm.permissions.map((p) => p.id));
  const count = ids.filter((id) => selected.has(id)).length;
  if (count === 0) return "unchecked";
  if (count === ids.length) return "checked";
  return "indeterminate";
}

function getServiceState(service, selected) {
  const ids = service.modules.flatMap((m) =>
  m.sub_modules.flatMap((sm) => sm.permissions.map((p) => p.id))
  );
  const count = ids.filter((id) => selected.has(id)).length;
  if (count === 0) return "unchecked";
  if (count === ids.length) return "checked";
  return "indeterminate";
}

function TriCheckbox({ state, onChange, disabled, id }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === "indeterminate";
    }
  }, [state]);

  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      className="cer-checkbox"
      checked={state === "checked"}
      onChange={onChange}
      disabled={disabled} />);


}

export default function PermissionTree({
  permissionMaster,
  selected,
  onTogglePermission,
  onToggleSubModule,
  onToggleModule,
  onToggleService,
  onToggleAll,
  searchQuery,
  expandedServices,
  expandedModules,
  expandedSubModules,
  onToggleServiceExpand,
  onToggleModuleExpand,
  onToggleSubModuleExpand,
  isAdminRole
}) {
  const totalPerms = permissionMaster.flatMap((s) =>
  s.modules.flatMap((m) => m.sub_modules.flatMap((sm) => sm.permissions))
  ).length;
  const allSelected = selected.size === totalPerms;
  const someSelected = selected.size > 0 && !allSelected;

  const allSelectRef = useRef(null);
  useEffect(() => {
    if (allSelectRef.current) {
      allSelectRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const q = searchQuery.toLowerCase().trim();
  const filteredTree = permissionMaster.map((service) => {
    const filteredMods = service.modules.map((mod) => {
      const filteredSubMods = mod.sub_modules.map((sm) => {
        const filteredPerms = q ?
        sm.permissions.filter(
          (p) =>
          p.name.toLowerCase().includes(q) ||
          sm.name.toLowerCase().includes(q) ||
          mod.name.toLowerCase().includes(q) ||
          service.name.toLowerCase().includes(q)
        ) :
        sm.permissions;
        return { ...sm, permissions: filteredPerms };
      }).filter((sm) => sm.permissions.length > 0);
      return { ...mod, sub_modules: filteredSubMods };
    }).filter((m) => m.sub_modules.length > 0);
    return { ...service, modules: filteredMods };
  }).filter((s) => s.modules.length > 0);

  return (
    <div className="cer-tree">
      <div className="cer-tree__select-all">
        <label className="cer-tree__select-all-label">
          <input
            ref={allSelectRef}
            type="checkbox"
            className="cer-checkbox"
            checked={allSelected}
            onChange={onToggleAll}
            disabled={isAdminRole} />
          
          <span className="cer-tree__select-all-text"><Trans i18nKey="formActions.selectAll" /></span>
        </label>
      </div>
      {filteredTree.length === 0 &&
      <div className="cer-tree__empty"><Trans i18nKey="roles.noPermissionsMatch" />{searchQuery}&rdquo;</div>
      }

      {filteredTree.map((service) => {
        const serviceState = getServiceState(service, selected);
        const isServiceOpen = expandedServices.has(service.id);
        return (
          <div key={service.id} className="cer-service">
            <div className="cer-service__row">
              <button type="button" className="cer-expand-btn" onClick={() => onToggleServiceExpand(service.id)} aria-label={isServiceOpen ? "Collapse" : "Expand"}>
                <ChevronDownIcon rotated={isServiceOpen} />
              </button>
              <TriCheckbox id={`svc-${service.id}`} state={isAdminRole ? "checked" : serviceState} onChange={() => onToggleService(service)} disabled={isAdminRole} />
              <label htmlFor={`svc-${service.id}`} className="cer-service__name">{service.name}</label>
            </div>

            {isServiceOpen &&
            <div className="cer-modules">
                {service.modules.map((mod) => {
                const modState = getModuleState(mod, selected);
                const isModOpen = expandedModules.has(mod.id);
                return (
                  <div key={mod.id} className="cer-module">
                      <div className="cer-module__row">
                        <button type="button" className="cer-expand-btn" onClick={() => onToggleModuleExpand(mod.id)} aria-label={isModOpen ? "Collapse" : "Expand"}>
                          <ChevronDownIcon rotated={isModOpen} />
                        </button>
                        <TriCheckbox id={`mod-${mod.id}`} state={isAdminRole ? "checked" : modState} onChange={() => onToggleModule(service, mod)} disabled={isAdminRole} />
                        <label htmlFor={`mod-${mod.id}`} className="cer-module__name">{mod.name}</label>
                      </div>

                      {isModOpen &&
                    <div className="cer-sub-modules">
                          {mod.sub_modules.map((sm) => {
                        const smState = getSubModuleState(sm, selected);
                        const isSmOpen = expandedSubModules.has(sm.id);
                        return (
                          <div key={sm.id} className="cer-sub-module">
                                <div className="cer-sub-module__row">
                                  <button type="button" className="cer-expand-btn" onClick={() => onToggleSubModuleExpand(sm.id)} aria-label={isSmOpen ? "Collapse" : "Expand"}>
                                    <ChevronDownIcon rotated={isSmOpen} />
                                  </button>
                                  <TriCheckbox id={`sm-${sm.id}`} state={isAdminRole ? "checked" : smState} onChange={() => onToggleSubModule(service, mod, sm)} disabled={isAdminRole} />
                                  <label htmlFor={`sm-${sm.id}`} className="cer-sub-module__name">{sm.name}</label>
                                </div>
                                {isSmOpen &&
                            <div className="cer-permissions">
                                    {sm.permissions.map((perm) =>
                              <div key={perm.id} className="cer-perm__row">
                                        <input
                                  id={`perm-${perm.id}`}
                                  type="checkbox"
                                  className="cer-checkbox"
                                  checked={isAdminRole ? true : selected.has(perm.id)}
                                  onChange={() => onTogglePermission(perm.id)}
                                  disabled={isAdminRole} />
                                
                                        <label htmlFor={`perm-${perm.id}`} className="cer-perm__name">{perm.name} <Trans i18nKey="roles.serviceId" /> {perm.id})</label>
                                      </div>
                              )}
                                  </div>
                            }
                              </div>);

                      })}
                        </div>
                    }
                    </div>);

              })}
              </div>
            }
          </div>);

      })}
    </div>);

}
